/**
 * WCC360 v2 — end of the run.
 *
 * On the last screen we do three things, in this order and never any other:
 *   1. Flush every answer to the database.
 *   2. Mark the runtime session complete.
 *   3. Ask the Engine Orchestrator to run the canonical pipeline — but only
 *      when the canonical pipeline can actually succeed.
 *
 * Step 3's precondition matters. The `evidence` stage counts rows in
 * `learner_evidence` for the session, and v2 answers can only reach that
 * table once the Command Center mints canonical items for the v2
 * interaction types (see `canon_pending_items`, key `v2-item-types`).
 * Until then, firing the pipeline would fail for every learner and put a
 * red stage on every result page for a reason that has nothing to do with
 * them. So we check first, and record why we skipped.
 */
import { startPipeline } from "@/lib/enginePipeline";
import { captureDnaSnapshot } from "./snapshot";
import { pushSkillsEvidence } from "./skillsSync";
import type { AssessmentV2State } from "./types";
import { track } from "@/lib/analytics";
import type { Band } from "./band";
import { bandParam } from "./band";
import { cloudStorage } from "@/lib/cloudStore";
import { supabase } from "@/integrations/supabase/client";
import { buildDerivedStudentPayload } from "./derivedPayload";
import { snapshotReport } from "@/lib/reportSnapshots";
import {

  completeSession,
  currentSessionId,
  flushOutbox,
  outboxSize,
  promotableScreenCount,
} from "./persistence";

export type PipelineDisposition = "started" | "blocked_canon" | "no_session";

export interface RunOutcome {
  sessionId: string | null;
  pipeline: PipelineDisposition;
  pendingWrites: number;
  completedAt: string;
}

function outcomeKey(band: Band) { return `wcc360.v2.outcome.${bandParam(band)}`; }

/** The result page reads this to know what happened at the end of the run. */
export function readRunOutcome(band: Band): RunOutcome | null {
  try {
    const raw = cloudStorage.getItem(outcomeKey(band));
    return raw ? (JSON.parse(raw) as RunOutcome) : null;
  } catch { return null; }
}

function writeRunOutcome(band: Band, outcome: RunOutcome) {
  try { cloudStorage.setItem(outcomeKey(band), JSON.stringify(outcome)); } catch { /* ignore */ }
}

/**
 * Close out the run. Always resolves — a failure here must never stop a
 * learner from seeing their result, and never discards an answer.
 */
export async function finishRun(
  band: Band,
  itemsResponded: number,
  state?: AssessmentV2State,
): Promise<RunOutcome> {
  const completedAt = new Date().toISOString();

  // The on-device Career DNA™ snapshot is captured here, not on the result
  // page: the learner may land anywhere next, and every other surface in the
  // shell reads it until the sealed report arrives.
  if (state) await captureDnaSnapshot(band, state);

  // Skills Intelligence™ evidence given during the walk goes to the Skills
  // Engine too. Best-effort: the report recomputes from the response log
  // regardless, so this never blocks the learner.
  if (state) void pushSkillsEvidence(state).catch(() => undefined);

  await flushOutbox(band).catch(() => undefined);
  const sessionId = await completeSession(band, itemsResponded).catch(() => currentSessionId(band));

  let pipeline: PipelineDisposition = "no_session";
  if (sessionId) {
    const promotable = await promotableScreenCount().catch(() => 0);
    if (promotable > 0) {
      try {
        await startPipeline(sessionId);
        pipeline = "started";
      } catch {
        pipeline = "blocked_canon";
      }
    } else {
      pipeline = "blocked_canon";
    }
  }

  const outcome: RunOutcome = {
    sessionId,
    pipeline,
    pendingWrites: outboxSize(band),
    completedAt,
  };
  writeRunOutcome(band, outcome);

  // Save after durable completion, not only when the learner visits /report.
  // Report page retries if this best-effort save cannot finish.
  if (state && sessionId && outcome.pendingWrites === 0) {
    void (async () => {
      const { data } = await supabase.auth.getUser();
      if (!data.user) return;
      const payload = await buildDerivedStudentPayload({ learnerId: data.user.id, state,
        displayName: data.user.user_metadata?.full_name ?? null, allowLocal: false });
      if (!payload) return;
      await supabase.functions.invoke("report-save", { body: { run_id: sessionId,
        payload: snapshotReport(payload, { state, sessionId, completedAt }) } });
    })().catch(() => undefined);
  }

  track("v2_assessment_completed", {
    surface: "assessment-v2",
    props: {
      band: bandParam(band),
      session_id: sessionId,
      pipeline,
      pending_writes: outcome.pendingWrites,
    },
  });


  return outcome;
}
